import TOML from "@iarna/toml";
import fs from "fs/promises";
import path from "path";
import { DEFAULT_TEAM_PATH } from "./constants";

/**
 * TypeScript types for the team config file.
 */
export interface TeamConfig {
  team: Record<string, string>; // email -> public key
  revoked?: Record<string, string>; // email -> ISO date string
  audit?: Record<string, string>; // ISO date string -> event description
  recovery?: {
    email: string;
    pubkey: string;
  };
}

/**
 * Read and parse the .envfortress.team TOML file.
 */
export async function readTeamConfig(
  teamPath: string = DEFAULT_TEAM_PATH
): Promise<TeamConfig> {
  const content = await fs.readFile(teamPath, "utf-8");
  console.log("[DEBUG] Raw team config file:", content);
  const parsed = TOML.parse(content) as unknown as TeamConfig;
  console.log("[DEBUG] Parsed team config:", parsed);
  // Ensure revoked and recovery sections are always present
  if (!parsed.revoked) parsed.revoked = {};
  if (!parsed.recovery) parsed.recovery = { email: "", pubkey: "" };
  return parsed;
}

/**
 * Stringify and write the .envfortress.team TOML file.
 * Always writes a valid TOML file, even if the team is empty.
 */
export async function writeTeamConfig(
  data: TeamConfig,
  teamPath: string = DEFAULT_TEAM_PATH
) {
  let out = "";
  if (data.team && Object.keys(data.team).length > 0) {
    out += "[team]\n";
    for (const [email, pubkey] of Object.entries(data.team)) {
      out += `"${email}" = "${pubkey}"\n`;
    }
    out += "\n";
  } else {
    out += "[team]\n\n";
  }
  // Write [recovery] section
  if (data.recovery && data.recovery.email && data.recovery.pubkey) {
    out += "[recovery]\n";
    out += `email = "${data.recovery.email}"\n`;
    out += `pubkey = "${data.recovery.pubkey}"\n\n`;
  }
  // Write [revoked] section
  if (data.revoked && Object.keys(data.revoked).length > 0) {
    out += "[revoked]\n";
    for (const [email, ts] of Object.entries(data.revoked)) {
      out += `"${email}" = "${ts}"\n`;
    }
    out += "\n";
  } else {
    out += "[revoked]\n\n";
  }
  // Write [audit] section and any other sections
  const { team, revoked, recovery, ...rest } = data;
  const restToml = TOML.stringify(rest as unknown as TOML.JsonMap);
  out += restToml;
  await fs.writeFile(teamPath, out, "utf-8");
  // Debug log to confirm file contents
  const fsSync = require("fs");
  if (fsSync.existsSync(teamPath)) {
    console.log("[DEBUG] Team config file after write:");
    console.log(fsSync.readFileSync(teamPath, "utf-8"));
  }
}

// Helper to get recovery key
export function getRecoveryKey(
  config: TeamConfig
): { email: string; pubkey: string } | null {
  if (config.recovery && config.recovery.email && config.recovery.pubkey) {
    return config.recovery;
  }
  return null;
}

// Helper to set recovery key
export function setRecoveryKey(
  config: TeamConfig,
  email: string,
  pubkey: string
) {
  config.recovery = { email, pubkey };
}
